import { useState, useEffect } from "react"
import { useMutation } from "@apollo/client"
import { useHistory } from "react-router-dom"
import { Form } from "react-bootstrap"
import Helmet from "react-helmet"
import OtpInput from "react-otp-input"
import { errorMessages } from "components/shared/ErrorToast/constants"
import Nav from "components/shared/Nav"
import ErrorToast from "components/shared/ErrorToast"
import FullPageLoader from "components/shared/FullPageLoader"
import Button from "components/shared/Button"
import { formatCooldown } from "shared/formatting/datetime"
import {
  useForceAuthEffect,
  useVerificationRedirectEffect
} from "components/Login/hooks"
import { Account } from "components/Login/operations"
import warningIcon from "../../shared/_assets/warning-icon.svg"
import { VerifyCode, ResendCode } from "./operations"

import styles from "./VerificationCode.module.scss"

const VerificationCode = () => {
  useForceAuthEffect({ authUrl: "/login" })
  useVerificationRedirectEffect()
  const history = useHistory()
  const [code, setCode] = useState("")
  const [validated, setValidated] = useState(false)
  const [codeError, setCodeError] = useState("")
  const [cooldown, setCooldown] = useState(0)

  const handleCodeChange = (newCode) => {
    setCode(newCode)
    if (codeError) {
      setCodeError("")
    }
  }

  const [verifyCode, { loading }] = useMutation(VerifyCode, {
    refetchQueries: [
      {
        query: Account,
        variables: { rsvpPortalId: process.env.REACT_APP_RSVP_PORTAL_ID }
      }
    ],
    onCompleted: ({ verifyCode: verifyCodeResult }) => {
      const { success, errors } = verifyCodeResult

      if (success) {
        history.replace("/sms-success")
      } else if (errors && errors.length > 0) {
        switch (errors[0]) {
          case "found_multiple":
            history.replace("/sms-multiple")
            break
          case "phone_number_already_verified":
            history.replace("/sms-taken")
            break
          default:
            setCodeError(errorMessages[errors[0]])
        }
      }
    }
  })

  const [resendCode, { loading: resendCodeLoading }] = useMutation(ResendCode, {
    onCompleted: ({ resendCode: resendCodeResult }) => {
      const { success, errors, retryAfterSeconds } = resendCodeResult

      if (success) {
        if (retryAfterSeconds && retryAfterSeconds > 0) {
          setCooldown(retryAfterSeconds)
        }
        setCodeError("")
      } else if (errors && errors.length > 0) {
        if (retryAfterSeconds && retryAfterSeconds > 0) {
          setCooldown(retryAfterSeconds)
        }
        setCodeError(errorMessages[errors[0]])
      }
    }
  })

  useEffect(() => {
    let timeoutId

    if (cooldown > 0) {
      timeoutId = setTimeout(() => {
        setCooldown((prevCooldown) => Math.max(prevCooldown - 1, 0))
      }, 1000)
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [cooldown])

  const handleResend = () => {
    resendCode({
      variables: {
        input: {}
      }
    })
  }

  const handleVerify = (e) => {
    e.preventDefault()

    setValidated(true)
    verifyCode({
      variables: {
        input: {
          code
        }
      }
    })
  }

  const resendButton =
    cooldown > 0 ? (
      <p className={styles.cooldown}>
        Didn&apos;t get a code? Resend ({formatCooldown(cooldown)})
      </p>
    ) : (
      <div className={styles.resendButtonContainer}>
        <p>Didn&apos;t get a code?</p>
        <button
          onClick={handleResend}
          disabled={resendCodeLoading}
          className={styles.resendButton}
          type="button"
        >
          Resend
        </button>
      </div>
    )

  if (loading || resendCodeLoading) {
    return <FullPageLoader message="Processing your request..." />
  }

  return (
    <div className="h-100">
      <Helmet>
        <title>Enter Verification Code</title>
      </Helmet>
      <Nav />
      <div className={styles.container}>
        <div className={styles.titleContainer}>
          <h2 className={styles.title}>Enter Verification Code</h2>
          <p className={styles.subtitle}>
            We’ve sent a 6-digit code to your phone number. This code will
            expire in 3 minutes.
          </p>
        </div>

        <Form
          noValidate
          validated={validated}
          onSubmit={handleVerify}
          className={styles.formContainer}
        >
          <div>
            <OtpInput
              value={code}
              onChange={handleCodeChange}
              numInputs={6}
              shouldAutoFocus
              inputType="tel"
              renderSeparator={null}
              renderInput={(props) => (
                <input
                  {...props}
                  className={styles.otpInput}
                />
              )}
              containerStyle={styles.otpContainer}
            />

            {codeError && (
              <div className={styles.errorContainer}>
                <img src={warningIcon} alt="error" />
                <div className={styles.errorMessage}>{codeError}</div>
              </div>
            )}
          </div>
          {resendButton}
          <Button type="submit" primary disabled={!code || codeError}>
            Verify
          </Button>
        </Form>
      </div>
      <ErrorToast />
    </div>
  )
}

export default VerificationCode
